import {
  addMonths,
  addYears,
  areIntervalsOverlapping,
  differenceInCalendarWeeks,
  format,
  getMinutes,
  isSameMonth,
  isWithinInterval,
  subYears,
} from 'date-fns'
import { formatInTimeZone, toZonedTime} from 'date-fns-tz';
import { LONDON_TIMEZONE, ONE_DAY } from './constants/global'
import { UserDetails } from './types/schemas/abc';



const AUGUST = 7
const OCTOBER = 9
const SEPTEMBER = 8



export const now = (): Date => toZonedTime(new Date(), LONDON_TIMEZONE)



export function getCookie(cookieName: string): string {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${cookieName}=`)
    return parts.length === 2 ? parts.pop()?.split(';').shift() || '' : ''
  }

  export function errorMessage(error: any): string | null {
    const errorData = error?.response?.data || {}
  
    // The error payload can be a simple string message or a complex validation error.
    // For the moment, we only cater for the former.
    if ('message' in errorData && typeof errorData.message === 'string') return errorData.message
    if ('detail' in errorData && typeof errorData.detail === 'string') return errorData.detail
    return null
  }

  export const capitaliseFirstLetter = (str?: string): string =>
  str ? str[0].toUpperCase() + str.slice(1) : ''


  export const formatShortYear = (year: string = shortYear()): string => {
    if (year?.length !== 4) return ''
    return `20${year.slice(0, 2)}-${year.slice(2, 4)}`
  }

  export const shortYear = (referenceDate?: Date): string => {
    const date = referenceDate ?? now()
    const currentYear = format(date, 'yy')
    const complementYear = format(addYears(date, date.getMonth() < SEPTEMBER ? -1 : 1), 'yy')
    // FIX: temporary until October
    return date.getMonth() < SEPTEMBER ? complementYear + currentYear : currentYear + complementYear
  }


/* Formats a date range as "D–D MMM or D MMM–D MMM" */
export const formatDateRange = (start: Date, end: Date): string =>
  format(start, 'd' + (isSameMonth(start, end) ? '' : ' MMM')) + format(end, '–d MMM')

const BASE_YEAR = 2021
export const validShortYears = (): string[] => {
  let years = []
  let date = now()
  let currentMonth = date.getMonth()

  // Add look-ahead in August
  if (currentMonth >= AUGUST && currentMonth < OCTOBER)
    years.unshift(shortYear(addMonths(date, OCTOBER - currentMonth)))

  while (date >= new Date(BASE_YEAR, OCTOBER, 1)) {
    years.unshift(shortYear(date))
    date = subYears(date, 1)
  }

  if (process.env.NODE_ENV === 'development' && !years.includes('2223')) years.push('2223')

  return years
}

interface ProfileStatus {
  isComplete: boolean;
  missingFields: string[];
}

/**
 * Checks the completeness of a Tutor's profile.
 * @param userDetails - The user details object.
 * @returns An object with a boolean and a list of missing fields.
 */
export const getTutorProfileStatus = (userDetails: UserDetails | undefined): ProfileStatus => {
  if (!userDetails) {
    return { isComplete: false, missingFields: ['All profile details'] };
  }

  const missing: string[] = [];
  
  // Add all the checks for a tutor
  if (!userDetails.profilePhotoUrl) missing.push('Profile photo');
  if (!userDetails.proofFileUrl)    missing.push('Proof of degree');
  if (!userDetails.description)     missing.push('Profile description');
  // if (!userDetails.subjects?.length) missing.push('Subjects taught');
  // if (!userDetails.levels?.length)   missing.push('Levels taught');
  if (!userDetails.expertise || Object.keys(userDetails.expertise).length === 0) {
    missing.push('Subjects taught');
  }
  if (!userDetails.university)      missing.push('University');
  if (!userDetails.college)         missing.push('College');
  if (!userDetails.degreeCourse)    missing.push('Degree course');
  if (!userDetails.gradYear)        missing.push('Graduation year');
  if (!userDetails.email_verified)  missing.push('Email verification');


  return {
    isComplete: missing.length === 0,
    missingFields: missing,
  };
};

/**
 * Checks the completeness of a Parent's profile.
 * @param userDetails - The user details object.
 * @returns An object with a boolean and a list of missing fields.
 */
export const getParentProfileStatus = (userDetails: UserDetails | undefined): ProfileStatus => {
  if (!userDetails) {
    return { isComplete: false, missingFields: ['All profile details'] };
  }

  const missing: string[] = [];

  // Add the specific checks for a parent
  if (!userDetails.description) missing.push('Profile description');
  if (!userDetails.email_verified)  missing.push('Email verification');
  if (!userDetails.expertise || Object.keys(userDetails.expertise).length === 0) {
    missing.push('Required subjects and levels');
  }

  return {
    isComplete: missing.length === 0,
    missingFields: missing,
  };
};